// src/components/fulfillment/OrgFormFillModal.tsx

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import type { OrgFormFilledBlock, OrgFormInstanceListItem } from "@/lib/opportunity/orgFormInstanceTypes";
import type { OrgFormTemplate } from "@/lib/opportunity/orgFormTemplateTypes";
import { getOrgFormInstance, saveOrgFormInstanceValues } from "@/lib/opportunity/orgFormInstanceClient";
import { fetchRepContactLocation } from "@/lib/opportunity/fetchRepContactLocation";
import {
  loadFormTemplateContactOverride,
  resolveFormContact,
} from "@/lib/opportunity/formContactResolver";
import {
  generateAndUploadFormPdf,
  renderFormPdfToBlob,
} from "@/components/opportunity/pdf/generateFormPdf";
import { OrgFormCalcBlockFillRow } from "./OrgFormCalcBlockFillRow";
import { OrgFormPdfPreviewModal } from "./OrgFormPdfPreviewModal";
import {
  getOrgFormBridgeFillContext,
  type OrgFormBridgeFillContext,
} from "@/lib/document-builder/bridge/orgFormBridgeClient";
import { OrgFormDocBridgeFillModal } from "@/components/document-builder/bridge/OrgFormDocBridgeFillModal";

export type OrgFormFillModalProps = {
  instanceId: string;
  onClose: () => void;
  onSaved: () => void;
};

function templateIncludesCalcs(
  template: OrgFormTemplate | null,
  blocks: OrgFormFilledBlock[],
): boolean {
  return (
    template?.include_calculations === true || blocks.some((b) => b.block_type === "calc")
  );
}

export function OrgFormFillModal(props: OrgFormFillModalProps) {
  const { instanceId, onClose, onSaved } = props;
  const org = useOrgSettings();
  const auth = useAuth();
  const [instance, setInstance] = useState<OrgFormInstanceListItem | null>(null);
  const [template, setTemplate] = useState<OrgFormTemplate | null>(null);
  const [blocks, setBlocks] = useState<OrgFormFilledBlock[]>([]);
  const [useRepContact, setUseRepContact] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"none" | "previewing" | "saving">("none");
  const [actionError, setActionError] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [bridged, setBridged] = useState(false);
  const [bridgeContext, setBridgeContext] = useState<OrgFormBridgeFillContext | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setLoadError(null);
      const [r, bridgeR] = await Promise.all([
        getOrgFormInstance(instanceId),
        getOrgFormBridgeFillContext(instanceId),
      ]);
      if (cancelled) return;
      setLoading(false);
      if (!r.ok) {
        setLoadError(r.error);
        return;
      }
      if (bridgeR.ok && bridgeR.data.ok && bridgeR.data.bridged) {
        setBridged(true);
        setBridgeContext(bridgeR.data);
        setUseRepContact(r.data.instance.use_rep_form_contact);
        return;
      }
      setBridged(false);
      setBridgeContext(null);
      setInstance(r.data.instance);
      setTemplate(r.data.template);
      setBlocks(r.data.blocks);
      setUseRepContact(r.data.instance.use_rep_form_contact);
    })();
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  const includeCalculations = useMemo(
    () => templateIncludesCalcs(template, blocks),
    [template, blocks],
  );
  const allowRep = template?.allow_rep_contact_override === true;
  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => a.sort_order - b.sort_order),
    [blocks],
  );

  function updateBlockText(blockId: string, text: string) {
    setBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, text_value: text } : b)),
    );
  }

  function updateBlockCalc(blockId: string, raw: string) {
    const n = Number(raw);
    setBlocks((prev) =>
      prev.map((b) =>
        b.id === blockId ? { ...b, calc_value: Number.isFinite(n) ? n : 0 } : b,
      ),
    );
  }

  function buildValuePayload() {
    return blocks.map((b) => ({
      block_id: b.id,
      text_value: b.block_type === "text" ? (b.text_value ?? null) : null,
      calc_value: b.block_type === "calc" ? (b.calc_value ?? 0) : null,
    }));
  }

  async function persistDraft() {
    return saveOrgFormInstanceValues({
      id: instanceId,
      values: buildValuePayload(),
      status: "draft",
      use_rep_form_contact: useRepContact,
    });
  }

  async function buildPdfArgs() {
    const title = instance?.template_display_name ?? "Form";
    const templateId = instance?.form_template_id ?? template?.id ?? "";
    const contactOverride = templateId ? await loadFormTemplateContactOverride(templateId) : null;
    const locId = auth?.profile?.active_location_id;
    const location = locId ? await fetchRepContactLocation(locId) : null;
    const { contact: senderContact } = resolveFormContact({
      orgRow: org.proposalContactRow,
      brandNameFallback: org.brandName,
      organizationNameFallback: org.organizationName,
      templateOverride: contactOverride,
      useRepContact,
      allowRepContactOverride: allowRep,
      profile: auth?.profile,
      location,
    });

    return {
      formInstanceId: instanceId,
      formTitle: title,
      brandName: org.brandName || org.organizationName,
      tagline: org.tagline,
      logoUrl: org.logoUrl,
      blocks,
      includeCalculations,
      senderContact,
    };
  }

  async function handlePreview() {
    setBusy("previewing");
    setActionError(null);
    const draft = await persistDraft();
    if (!draft.ok) {
      setBusy("none");
      setActionError(draft.error);
      return;
    }
    const r = await renderFormPdfToBlob(await buildPdfArgs());
    setBusy("none");
    if (!r.ok) {
      setActionError(r.error);
      return;
    }
    setPreviewBlob(r.blob);
  }

  async function handleSaveDirect() {
    setBusy("saving");
    setActionError(null);
    const draft = await persistDraft();
    if (!draft.ok) {
      setBusy("none");
      setActionError(draft.error);
      return;
    }
    const r = await generateAndUploadFormPdf(await buildPdfArgs());
    setBusy("none");
    if (!r.ok) {
      setActionError(r.error);
      return;
    }
    onSaved();
    onClose();
  }

  if (loading) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
        <div className="rounded-xl bg-white px-6 py-4 text-sm dark:bg-zinc-900">Loading form…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
        <div className="max-w-md rounded-xl bg-white p-5 dark:bg-zinc-900">
          <p className="text-sm text-red-700 dark:text-red-300">{loadError}</p>
          <button type="button" onClick={onClose} className="mt-3 text-sm text-indigo-600">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (bridged && bridgeContext) {
    return (
      <OrgFormDocBridgeFillModal
        instanceId={instanceId}
        context={bridgeContext}
        useRepContact={useRepContact}
        onClose={onClose}
        onSaved={onSaved}
      />
    );
  }

  if (!instance) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
        <div className="max-w-md rounded-xl bg-white p-5 dark:bg-zinc-900">
          <p className="text-sm text-red-700 dark:text-red-300">Form not found</p>
          <button type="button" onClick={onClose} className="mt-3 text-sm text-indigo-600">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 p-4 sm:items-center">
        <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-zinc-900">
          <div className="border-b border-gray-200 px-5 py-3 dark:border-zinc-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
              {instance.template_display_name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              Status: {instance.status}
              {actionError ? <span className="ml-2 text-red-600">{actionError}</span> : null}
            </p>
            {allowRep ? (
              <label className="mt-2 flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-300">
                <input
                  type="checkbox"
                  checked={useRepContact}
                  onChange={(e) => setUseRepContact(e.target.checked)}
                />
                Use my rep / location contact on this form PDF
              </label>
            ) : null}
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {sortedBlocks.map((block) => {
              if (block.block_type === "calc") {
                if (!includeCalculations) return null;
                return (
                  <OrgFormCalcBlockFillRow
                    key={block.id}
                    block={block}
                    allBlocks={blocks}
                    onCalcChange={updateBlockCalc}
                  />
                );
              }

              return (
                <label key={block.id} className="block text-xs">
                  <span className="font-medium text-gray-700 dark:text-zinc-200">
                    {block.label || "Text"}
                  </span>
                  {block.helper_text ? (
                    <span className="mt-0.5 block text-gray-500">{block.helper_text}</span>
                  ) : null}
                  <textarea
                    value={block.text_value ?? ""}
                    onChange={(e) => updateBlockText(block.id, e.target.value)}
                    rows={block.sort_order === 0 ? 6 : 4}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800"
                  />
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-zinc-700">
            <button
              type="button"
              onClick={onClose}
              disabled={busy !== "none"}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handlePreview()}
              disabled={busy !== "none"}
              className="rounded-lg border border-indigo-300 px-3 py-1.5 text-sm text-indigo-700 disabled:opacity-50"
            >
              {busy === "previewing" ? "Rendering…" : "Preview"}
            </button>
            <button
              type="button"
              onClick={() => void handleSaveDirect()}
              disabled={busy !== "none"}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {busy === "saving" ? "Saving…" : "Save PDF"}
            </button>
          </div>
        </div>
      </div>

      <OrgFormPdfPreviewModal
        open={previewBlob !== null}
        blob={previewBlob}
        formInstanceId={instanceId}
        formTitle={instance.template_display_name}
        onSaved={() => {
          setPreviewBlob(null);
          onSaved();
          onClose();
        }}
        onClose={() => setPreviewBlob(null)}
      />
    </>
  );
}
